import type {
  ButtonAction,
  ExperienceBlockDraft,
  ExperiencePageDraft,
  TempsDraft,
} from '@matter_extractor/shared';
import { ANIMATION_SPEED_MS, EXPERIENCE_NOTE_STARS } from '@matter_extractor/shared';
import type { ReaderValues } from './contracts';
import type { ReaderMotion } from './reader-theme';

/**
 * WHEN each temps appears, and what that lets a button do (§9.3, H26, H27,
 * H30).
 *
 * All of it PURE, and none of it inside a component: the arithmetic of a delay
 * and the rule that greys a button are exactly the two things a reviewer wants
 * to read without a DOM, and `vitest.config.ts` measures coverage on `src/**`
 * with no layout to help.
 */

/* ------------------------------------------------------ what the page shows */

/**
 * Whether the page SHOWS a block (§9.2, H70): the display conditions of the
 * block, evaluated by `isBlockDisplayed` of `shared` on the sources the caller
 * holds. A predicate and not the sources, so every rule below stays a question
 * about blocks and never learns what a condition is.
 */
export type BlockDisplayed = (block: ExperienceBlockDraft) => boolean;

/** Every block shows - a reader handed no condition to evaluate. */
export const EVERY_BLOCK_SHOWN: BlockDisplayed = () => true;

/**
 * A temps EVERY block of which is masked (§9.2, H70): « sauté, son délai
 * compris » - the person does not wait for an empty temps.
 *
 * An EMPTY temps is not one: it holds no masked block, so it keeps the delay its
 * author gave it, exactly as before the conditions existed.
 */
export function tempsMasked(temps: TempsDraft, displayed: BlockDisplayed): boolean {
  return temps.blocks.length > 0 && !temps.blocks.some(displayed);
}

/* ---------------------------------------------------------------- the temps */

/** A Son has no entry to play (§9.3), and `none` is not an animation. */
function blockAnimates(block: ExperienceBlockDraft): boolean {
  return block.type !== 'sound' && block.animation !== 'none';
}

/**
 * How long the entry animations of one temps take.
 *
 * All the blocks of a temps start together and share its rapidité (H27: « un
 * bloc ne force pas sa rapidité »), so the temps is over after ONE duration,
 * not after the sum of its blocks. A temps where nothing animates is over the
 * moment it appears - which is also what reduced motion makes of every temps,
 * and what a masked block makes of itself (H70): only what the page shows plays.
 */
export function tempsAnimationMs(
  temps: TempsDraft,
  motion: ReaderMotion,
  displayed: BlockDisplayed = EVERY_BLOCK_SHOWN,
): number {
  if (motion === 'reduced') return 0;
  return temps.blocks.some((block) => displayed(block) && blockAnimates(block))
    ? ANIMATION_SPEED_MS[temps.speed]
    : 0;
}

/**
 * How long to wait, from the moment temps `index - 1` appeared, before temps
 * `index` appears. `null` when it waits for a click instead (H30).
 *
 * « Le délai d'un temps se compte depuis la fin des animations du temps
 * précédent » (§9.3), so the answer is the previous temps' animation plus this
 * one's own delay. Under reduced motion the animations are over at once and
 * the delay alone remains, which is « tout apparaît d'un coup et les délais
 * entre les temps restent » read literally.
 *
 * Temps 1 answers 0: it starts with the page, and its delay does not even get
 * a control (H27).
 *
 * A temps every block of which is MASKED (§9.2, H70) is skipped, its delay
 * included: it is due the moment the previous temps' animations are over, and
 * it takes no time of its own - it animates nothing - so the temps after it
 * counts its delay from the last temps the person actually saw. A masked temps
 * that starts at a CLICK still waits for it: the Bouton that plays it is never
 * masked (`condition-on-exit`), and a press is the author's rhythm, not a wait.
 *
 * And the FIRST temps the page shows starts with the page, like temps 1 (a
 * decision of the orchestrator on issue #150): when every temps before it is
 * masked, the person would otherwise look at an empty page for its own delay,
 * which is exactly the wait H70 removes. Its start is not read at all - a delay
 * or a click alike - because nothing it could wait for has appeared: in a
 * version a Bouton carries no condition (`condition-on-exit`), so a temps
 * holding one is never entirely masked and no Bouton stands before it.
 */
export function tempsStartDelayMs(
  temps: readonly TempsDraft[],
  index: number,
  motion: ReaderMotion,
  displayed: BlockDisplayed = EVERY_BLOCK_SHOWN,
): number | null {
  if (index <= 0) return 0;
  const current = temps[index];
  const previous = temps[index - 1];
  if (current === undefined || previous === undefined) return 0;
  if (temps.slice(0, index).every((before) => tempsMasked(before, displayed))) return 0;
  if (current.start.kind === 'click') return null;
  const own = tempsMasked(current, displayed) ? 0 : current.start.seconds * 1000;
  return tempsAnimationMs(previous, motion, displayed) + own;
}

/** Every block of the temps that have appeared, in reading order. */
export function appearedBlocks(
  page: ExperiencePageDraft,
  revealed: number,
): ExperienceBlockDraft[] {
  return page.temps.slice(0, Math.max(0, revealed)).flatMap((temps) => temps.blocks);
}

/** Which temps holds a block, or `null` when no temps does. */
export function tempsIndexOfBlock(page: ExperiencePageDraft, blockId: string): number | null {
  const index = page.temps.findIndex((temps) => temps.blocks.some((block) => block.id === blockId));
  return index === -1 ? null : index;
}

/* -------------------------------------------------------------- the buttons */

/**
 * What pressing a Bouton does, given what has already appeared (§9.2, H30).
 *
 * - `next-page` - the default, and what a `next-temps` button of the LAST temps
 *   falls back to: « un bouton du dernier temps n'a que la page suivante ». An
 *   « Envoyer le retour » (`submit`, retour-experience.md §5) is one too: it
 *   leaves the page with its values, and it is the SERVER that tells a submit
 *   from an advance, by the action of the Bouton named in the request.
 * - `next-temps` - the temps it plays, in place of that temps' own delay.
 * - `spent` - the temps it played is out, so the button is gone: « il disparaît
 *   alors une fois le temps joué ».
 */
export type ButtonBehaviour =
  | { readonly kind: 'next-page' }
  | { readonly kind: 'next-temps'; readonly tempsIndex: number }
  | { readonly kind: 'spent' };

export function buttonBehaviour(
  page: ExperiencePageDraft,
  blockId: string,
  action: ButtonAction,
  revealed: number,
): ButtonBehaviour {
  if (action === 'next-page' || action === 'submit') return { kind: 'next-page' };
  const own = tempsIndexOfBlock(page, blockId);
  if (own === null) return { kind: 'next-page' };
  const next = own + 1;
  if (next >= page.temps.length) return { kind: 'next-page' };
  return revealed > next ? { kind: 'spent' } : { kind: 'next-temps', tempsIndex: next };
}

/* --------------------------------------------------------- what blocks a page */

/**
 * Why a page cannot be left yet.
 *
 * `empty` is the rule of §9.2 word for word - a required block, already
 * appeared, with nothing in it. `email` is the browser half of the double check
 * of §9.2 (« vérifié deux fois : au navigateur pour la main de la personne, au
 * serveur à l'avancée de page »): a single-line Input of format email that
 * holds something which is not one. Letting that through would trade a grey
 * button for a refusal from the server after the press, which is the worse of
 * the two.
 */
export type ReaderBlocker = {
  readonly blockId: string;
  readonly kind: 'empty' | 'email';
  readonly blockType: ExperienceBlockDraft['type'];
};

/** What the reader holds for a block, as a string. A tick is not a text. */
export function readerText(values: ReaderValues, blockId: string): string {
  const value = values[blockId];
  return typeof value === 'string' ? value : '';
}

/** What the reader holds for a block, as a tick. */
export function readerTicked(values: ReaderValues, blockId: string): boolean {
  return values[blockId] === true;
}

/**
 * The note a Note block holds: the digit of its star, 1 to 5, or 0 when it holds
 * none (retour-experience.md §4.2). A value that is not one - a text stored
 * under the block by some other hand - reads as « no note » rather than as a
 * star somebody never gave.
 */
export function readerNote(value: string): number {
  const note = Number(value);
  return Number.isInteger(note) && note >= 1 && note <= EXPERIENCE_NOTE_STARS ? note : 0;
}

/**
 * Deliberately permissive: one `@`, something on each side, a dot in the
 * domain. The strict answer is the server's (§9.2), and a client pattern that
 * refused a legal address would be a page nobody can leave.
 */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isReaderEmail(value: string): boolean {
  return EMAIL.test(value);
}

/**
 * What stops the page, among the blocks that have ALREADY APPEARED (H30).
 *
 * « Seuls les obligatoires déjà apparus grisent les boutons » is the whole
 * point of counting from `revealed` rather than from the page: a required field
 * sitting in a temps that has not played yet must not grey the button that
 * plays it. And only among the blocks the page SHOWS: « un champ obligatoire
 * masqué ne grise aucun bouton » (§9.2, H70) - the browser half of
 * `advanceProblems`, which skips the very same blocks (H72).
 */
export function readerBlockers(
  page: ExperiencePageDraft,
  revealed: number,
  values: ReaderValues,
  displayed: BlockDisplayed = EVERY_BLOCK_SHOWN,
): ReaderBlocker[] {
  const blockers: ReaderBlocker[] = [];
  for (const block of appearedBlocks(page, revealed)) {
    if (!displayed(block)) continue;
    if (block.type === 'checkbox') {
      if (block.required && !readerTicked(values, block.id)) {
        blockers.push({ blockId: block.id, kind: 'empty', blockType: 'checkbox' });
      }
      continue;
    }
    // A Sélection and a Note are refused like an Input: required, and holding
    // nothing (retour-experience.md §10). `advanceProblems` reads the same.
    if (block.type === 'selection' || block.type === 'note') {
      if (block.required && readerText(values, block.id).trim() === '') {
        blockers.push({ blockId: block.id, kind: 'empty', blockType: block.type });
      }
      continue;
    }
    if (block.type !== 'input') continue;
    const text = readerText(values, block.id).trim();
    if (block.required && text === '') {
      blockers.push({ blockId: block.id, kind: 'empty', blockType: 'input' });
      continue;
    }
    if (
      block.lines === 'single' &&
      block.format === 'email' &&
      text !== '' &&
      !isReaderEmail(text)
    ) {
      blockers.push({ blockId: block.id, kind: 'email', blockType: 'input' });
    }
  }
  return blockers;
}

/**
 * The sentence under a blocked button - why it is grey.
 *
 * The mockup puts one there (« Cochez la case pour continuer. » under the
 * disabled « Commencer » of `Lecteur.dc.html`), and a grey control that says
 * nothing is the one thing a person cannot get past. A missing value is named
 * before a malformed one: somebody who has not filled a field has not reached
 * the question of its format.
 */
export function advanceHint(blockers: readonly ReaderBlocker[]): string | null {
  const missing = blockers.filter((blocker) => blocker.kind === 'empty');
  if (missing.length === 0) {
    return blockers.length === 0 ? null : "Vérifiez l'adresse email pour continuer.";
  }
  if (missing.every((blocker) => blocker.blockType === 'checkbox')) {
    return missing.length === 1
      ? 'Cochez la case pour continuer.'
      : 'Cochez les cases pour continuer.';
  }
  // The questions of the feedback page are not « champs »: a person who leaves
  // one of them empty is told to answer it.
  if (
    missing.every((blocker) => blocker.blockType === 'selection' || blocker.blockType === 'note')
  ) {
    return missing.length === 1
      ? 'Répondez à la question pour continuer.'
      : 'Répondez aux questions pour continuer.';
  }
  return 'Complétez les champs obligatoires pour continuer.';
}

/**
 * The entry animation a block actually plays.
 *
 * A Son never has one (§9.3), whatever its row says: `blockShape` keeps
 * `animation` on the common shape so a stored `fade` on a sound stays READABLE
 * rather than making a version nobody can open, and this is where that reading
 * is turned back into « aucune ».
 */
export function blockAnimation(block: ExperienceBlockDraft): ExperienceBlockDraft['animation'] {
  return block.type === 'sound' ? 'none' : block.animation;
}
